import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ParliamentarianHeader } from '@/components/ParliamentarianHeader';
import { NavigationHeader } from '@/components/NavigationHeader';
import { auth, db } from '@/firebase';
import { onAuthStateChanged, getIdTokenResult } from 'firebase/auth';
import type { UserRole } from '@/services/auth';
import { listInstitutions, listCouncilors, listAmendmentsByCouncilor, updateAmendment, deleteAmendment, getSettings, type Institution, type Amendment, type Councilor, getUserCouncilorId, getUserRole, listDescriptionTemplates, type DescriptionTemplate, addOrMergeAmendmentSecure, logRuleViolation } from '@/services/firestore';
import { computeHealthTotals, isHealthCategory, wouldBreakHealthRule } from '@/lib/health-allocation';
import { onSnapshot, collection, doc, query, where } from 'firebase/firestore';
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { applyCategoryFilter } from '@/lib/category-filter';
import { getCategoryColors } from '@/lib/category-colors';
import { InstitutionAmendmentModal } from '@/components/InstitutionAmendmentModal';
import { todayIsoDateCuiaba, formatIsoDateToPtBr, cuiabaDateTimeToUtcMillis, nowCuiabaUtcMillis, toCents, fromCents } from '@/lib/utils';

/**
 * Calcula a meta de Saúde conforme regra: 50% do valor total + 1 real.
 * Exemplo: para `1000`, retorna `501`.
 *
 * @param valorTotal Valor numérico positivo para cálculo
 * @returns Número arredondado para cima com o resultado
 * @throws Erro quando valor não é numérico ou quando negativo
 *
 * @example
 * calcularMetaSaude(1000) // 501
 * calcularMetaSaude(1001) // 502
 */
export function calcularMetaSaude(valorTotal: number): number {
  if (typeof valorTotal !== 'number' || Number.isNaN(valorTotal)) {
    throw new Error('Valor total deve ser um número');
  }
  if (valorTotal < 0) {
    throw new Error('Valor total não pode ser negativo');
  }
  // Regra: meta = metade do limite + R$ 1,00, preservando centavos (half-up)
  const cents = toCents(valorTotal);
  const halfCents = Math.round(cents * 0.5);
  return fromCents(halfCents + 100);
}

// Componente para formatação de valor monetário (com centavos)
const MoneyInput = ({ id, name, placeholder, value, onChange, required = false, disabled = false, className, ariaInvalid }: {
  id: string;
  name: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  ariaInvalid?: boolean;
}) => {
  const formatCurrency = (value: string) => {
    // Remove tudo que não é dígito
    const cleanValue = value.replace(/\D/g, '');

    // Interpreta os últimos dois dígitos como centavos
    const cents = parseInt(cleanValue || '0', 10);
    const amount = cents / 100;

    // Formata como moeda brasileira com centavos
    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatCurrency(e.target.value);
    e.target.value = formattedValue;
    onChange?.(e.target.value);
  };

  return (
    <Input
      id={id}
      name={name}
      placeholder={placeholder}
      defaultValue={value}
      onChange={handleChange}
      required={required}
      disabled={disabled}
      className={className}
      aria-invalid={ariaInvalid}
    />
  );
};

type TestOverrides = { now?: () => number; wait?: (ms: number) => Promise<void> };
export default function ParliamentarianArea(props: { testOverrides?: TestOverrides } = {}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  // Proteção de rota é tratada pelo ProtectedRoute
  const [userName, setUserName] = useState('');
  const [userType, setUserType] = useState<'admin' | 'vereador' | undefined>(undefined);
  const [userUid, setUserUid] = useState<string | null>(null);
  const [councilorId, setCouncilorId] = useState<string | null>(null);

  const isAdmin = userType === 'admin';
  const [councilor, setCouncilor] = useState<Councilor & { position: string }>({
    id: '',
    name: '',
    party: '',
    position: 'Vereador',
    totalLimit: 0,
    photoUrl: '/src/assets/parlamentar-photo.jpg',
  });

  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const institutionsRef = useRef<Institution[]>([]);

  type AmendmentView = Amendment & { institutionName: string };
  const [amendments, setAmendments] = useState<AmendmentView[]>([]);
  // Ref espelhando as emendas em tempo real para verificações assíncronas
  const amendmentsRef = useRef<AmendmentView[]>([]);

  // Estado do sistema - bloqueio de alocações via Firestore
  const [allocationsLocked, setAllocationsLocked] = useState(false);
  const [lockSchedule, setLockSchedule] = useState({ enabled: false, date: '', time: '' });
  const [countdownText, setCountdownText] = useState('');
  const [countdownUrgent, setCountdownUrgent] = useState(false);
  const [countdownWarning, setCountdownWarning] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [editingAmendment, setEditingAmendment] = useState<string | null>(null);
  const editDialogRef = useRef<HTMLDivElement | null>(null);
  // Bloqueia a rolagem da página enquanto o card de edição estiver aberto
  useEffect(() => {
    if (editingAmendment) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow || '';
      };
    }
  }, [editingAmendment]);
  // Garante foco inicial acessível ao abrir o modal
  useEffect(() => {
    if (editingAmendment && editDialogRef.current) {
      editDialogRef.current.focus();
    }
  }, [editingAmendment]);
  const [templates, setTemplates] = useState<DescriptionTemplate[]>([]);
  const [desc, setDesc] = useState('');
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [editTemplateDialogFor, setEditTemplateDialogFor] = useState<string | null>(null);

  // Modal de Detalhes (Visualização)
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedAmendmentDetail, setSelectedAmendmentDetail] = useState<AmendmentView | null>(null);

  // Combobox pesquisável para Instituição
  const [institutionOpen, setInstitutionOpen] = useState(false);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<string>('');
  const [institutionQuery, setInstitutionQuery] = useState('');
  const [debouncedInstitutionQuery, setDebouncedInstitutionQuery] = useState('');
  const [isSearchingInstitution, setIsSearchingInstitution] = useState(false);
  const [filterByInitial, setFilterByInitial] = useState(false);
  const [phoneticMode, setPhoneticMode] = useState(false);
  const [halfMatchMode, setHalfMatchMode] = useState(false);
  // Aceita qualquer categoria cadastrada no sistema; 'all' significa sem filtro
  const [categoryFilter, setCategoryFilter] = useState<'all' | string>('all');

  // Duplicidade por Instituição: aviso e fluxo de edição/cancelamento
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [existingForSelected, setExistingForSelected] = useState<AmendmentView | null>(null);

  // ======= Categorias reais do sistema (dinâmico e fiel ao cadastro) =======
  const [selectedRealCategory, setSelectedRealCategory] = useState<string | 'all'>('all');
  const realCategories = useMemo(() => {
    const cats = new Set<string>();
    institutionsRef.current.forEach((i) => {
      const c = (i.category || '').trim();
      if (c) cats.add(c);
    });
    return Array.from(cats).sort((a, b) => a.localeCompare(b));
  }, [institutions]);
  const groupedByRealCategory = useMemo(() => {
    const groups: Record<string, AmendmentView[]> = {};
    for (const a of amendments) {
      const inst = institutionsRef.current.find((i) => i.id === a.institutionId);
      const cat = (inst?.category || '').trim() || 'Sem Categoria';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(a);
    }
    return groups;
  }, [amendments]);

  const normalizeText = (s: string) => (
    (s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // remove acentos
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ') // remove especiais mantendo espaços
      .replace(/\s+/g, ' ')
      .trim()
  );

  const getInitials = (s: string) => (
    normalizeText(s)
      .split(' ')
      .map((w) => w[0] || '')
      .join('')
  );

  const phoneticKey = (s: string) => (
    normalizeText(s)
      .replace(/[hw]/g, '')
      .replace(/[aeiou]/g, '') // remove vogais
      .replace(/([a-z])\1+/g, '$1') // remove duplicatas
  );

  const levenshtein = (a: string, b: string) => {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    const dp = Array(b.length + 1).fill(0).map((_, i) => i);
    for (let i = 1; i <= a.length; i++) {
      let prev = i - 1;
      dp[0] = i;
      for (let j = 1; j <= b.length; j++) {
        const temp = dp[j];
        dp[j] = Math.min(
          dp[j] + 1, // deletion
          dp[j - 1] + 1, // insertion
          prev + (a[i - 1] === b[j - 1] ? 0 : 1) // substitution
        );
        prev = temp;
      }
    }
    return dp[b.length];
  };

  const similarity = (a: string, b: string) => {
    if (!a || !b) return 0;
    const dist = levenshtein(a, b);
    return 1 - dist / Math.max(a.length, b.length);
  };

  useEffect(() => {
    setIsSearchingInstitution(true);
    const t = setTimeout(() => {
      setDebouncedInstitutionQuery(institutionQuery.trim());
      setIsSearchingInstitution(false);
    }, 200);
    return () => clearTimeout(t);
  }, [institutionQuery]);

  // Logs detalhados para rastrear a busca
  useEffect(() => {
    const qRaw = debouncedInstitutionQuery;
    const q = normalizeText(qRaw);
    const instCount = institutions.length;
    const exact = institutions.find((i) => normalizeText(i.name) === q);
    console.debug('[BuscaInstituicao]', {
      queryRaw: qRaw,
      queryNormalized: q,
      institutions: instCount,
      exactMatchFound: !!exact,
      exactMatchName: exact?.name,
    });
  }, [debouncedInstitutionQuery, institutions]);

  const normalizedInstitutions = useMemo(() => (
    institutions.map((inst) => {
      const n = normalizeText(inst.name);
      const tokens = n.split(' ');
      return {
        ...inst,
        _n: n,
        _tokens: tokens,
        _initials: getInitials(inst.name),
        _phonetic: phoneticKey(inst.name),
      };
    })
  ), [institutions]);

  const filteredInstitutions = useMemo(() => {
    const qRaw = debouncedInstitutionQuery;
    const q = normalizeText(qRaw);
    // Aplica filtro por categoria antes de processar busca para performance
    const baseInstitutions = applyCategoryFilter(normalizedInstitutions, categoryFilter === 'all' ? null : categoryFilter);
    if (!q) return baseInstitutions;

    const qPh = phoneticKey(qRaw);

    const results: Array<{ inst: typeof normalizedInstitutions[number]; score: number }> = [];

    for (const inst of baseInstitutions) {
      const n = inst._n;
      const tokens = inst._tokens;
      // Match exato (normalizado) com prioridade máxima, independente de filtros avançados
      if (n === q) {
        results.push({ inst, score: 1000 });
        continue;
      }

      // Aplicar filtros avançados (restritivos) se ativos
      if (filterByInitial && !inst._initials.startsWith(q)) continue;

      if (halfMatchMode) {
        const halfMatched = tokens.some((t) => {
          if (!t) return false;
          const mid = Math.ceil(t.length / 2);
          const left = t.slice(0, mid);
          const right = t.slice(mid);
          return left.startsWith(q) || right.startsWith(q) || left.includes(q) || right.includes(q);
        });
        if (!halfMatched) continue;
      }

      let score = 0;
      // Prioridade: prefixo do nome completo
      if (n.startsWith(q)) score = 100;
      // Prefixo de qualquer token
      else if (tokens.some((t) => t.startsWith(q))) score = 90;
      // Substring em qualquer posição
      else if (n.includes(q)) score = 70;
      else {
        // Similaridade aproximada (apenas quando query >= 2)
        if (q.length >= 2) {
          const sim = similarity(n, q);
          if (sim >= 0.8) score = 50 + Math.round(sim * 50);
        }
      }

      // Phonética opcional: pequeno boost ou match dedicado
      if (phoneticMode) {
        const ph = inst._phonetic;
        if (ph.includes(qPh)) score += 10;
        else {
          const simPh = similarity(ph, qPh);
          if (simPh >= 0.75) score += Math.round(simPh * 10);
        }
      }

      if (score > 0) results.push({ inst, score });
    }

    // Ordena por score desc e nome asc para consistência
    results.sort((a, b) => (b.score - a.score) || a.inst.name.localeCompare(b.inst.name));

    // Limita para performance (top 50)
    return results.slice(0, 50).map((r) => r.inst);
  }, [normalizedInstitutions, debouncedInstitutionQuery, filterByInitial, phoneticMode, halfMatchMode, categoryFilter]);

  useEffect(() => {
    // Carrega modelos de descrição ativos para uso no picker
    listDescriptionTemplates()
      .then((items) => setTemplates(items))
      .catch(() => void 0);
  }, []);

  const validateDescriptionFormat = (text: string) => {
    const trimmed = (text || '').trim();
    if (trimmed.length < 3 || trimmed.length > 600) return false;
    // Permite letras (inclui acentos), números, espaço e pontuações simples
    const re = /^[\p{L}\p{N}\s.,;:()\/\-]+$/u;
    return re.test(trimmed);
  };

  // Assinaturas em tempo real para usuário, instituições, configurações, vereador e emendas
  useEffect(() => {
    let unsubInstitutions: (() => void) | undefined;
    let unsubSettings: (() => void) | undefined;
    let unsubCouncilor: (() => void) | undefined;
    let unsubAmendments: (() => void) | undefined;

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }
      try {
        // Busca role e councilorId com tolerância a permissão negada
        let role: UserRole | undefined;
        let cId: string | undefined;
        try {
          role = await getUserRole(user.uid);
        } catch (e) {
          try {
            const token = await getIdTokenResult(user);
            role = token.claims?.role as UserRole;
          } catch { void 0; }
        }
        try {
          cId = await getUserCouncilorId(user.uid);
        } catch (e) {
          console.warn('Permissão negada ao ler users/{uid}, tentando claims.', e);
          try {
            const token = await getIdTokenResult(user);
            cId = (token.claims?.councilorId as string) || undefined;
          } catch { void 0; }
        }

        setUserName(user.displayName || user.email || 'Vereador');
        setUserType(role ?? 'vereador');
        setUserUid(user.uid || null);

        // Instituições em tempo real
        if (!unsubInstitutions) {
          unsubInstitutions = onSnapshot(
            collection(db, 'institutions'),
            (snap) => {
              const insts: Institution[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Institution, 'id'>) }));
              institutionsRef.current = insts;
              setInstitutions(insts);
            },
            async (err) => {
              console.warn('Falha no listener de instituições. Tentando fallback de leitura única.', err);
              try {
                const insts = await listInstitutions();
                institutionsRef.current = insts;
                setInstitutions(insts);
              } catch (e) {
                console.warn('Fallback listInstitutions também falhou.', e);
              }
            }
          );
          // Prefetch: tenta carregar uma vez de forma direta para garantir dados iniciais
          listInstitutions()
            .then((insts) => {
              if (insts?.length) {
                institutionsRef.current = insts;
                setInstitutions(insts);
              }
            })
            .catch(() => void 0);
        }

        // Configurações do sistema (bloqueio)
        if (!unsubSettings) {
          unsubSettings = onSnapshot(
            doc(db, 'system', 'settings'),
            (snap) => {
              const data = snap.data() as { allocationsLocked?: boolean; lockSchedule?: { enabled: boolean; date: string; time: string } } | undefined;
              setAllocationsLocked(data?.allocationsLocked ?? false);
              setLockSchedule(data?.lockSchedule ?? { enabled: false, date: '', time: '' });
            },
            (err) => console.warn('Falha no listener de configurações.', err)
          );
        }

        if (cId) {
          setCouncilorId(cId);
          // Dados do vereador em tempo real
          if (unsubCouncilor) unsubCouncilor();
          unsubCouncilor = onSnapshot(
            doc(db, 'councilors', cId),
            (snap) => {
              if (!snap.exists()) return;
              const data = snap.data() as Omit<Councilor, 'id'>;
              setCouncilor({
                id: snap.id,
                name: data.name,
                party: data.party,
                totalLimit: data.totalLimit,
                position: 'Vereador',
                photoUrl: data.photoUrl || '/src/assets/parlamentar-photo.jpg',
              });
            },
            (err) => console.warn('Falha no listener do vereador.', err)
          );

          // Emendas em tempo real do vereador
          if (unsubAmendments) unsubAmendments();
          unsubAmendments = onSnapshot(
            query(collection(db, 'amendments'), where('councilorId', '==', cId)),
            (snap) => {
              const insts = institutionsRef.current;
              const amends: Amendment[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Amendment, 'id'>) }));
              const view = amends.map((a) => ({
                ...a,
                institutionName: insts.find((i) => i.id === a.institutionId)?.name || '—',
              }));
              amendmentsRef.current = view;
              setAmendments(view);
            },
            (err) => console.warn('Falha no listener de emendas.', err)
          );
        } else {
          // Tentativa de auto-vínculo por email via servidor admin
          let linked = false;
          try {
            if ((role ?? 'vereador') === 'vereador' && user.email) {
              const { adminHealth, adminPost } = await import('@/services/admin-api');
              const ok = await adminHealth();
              if (!ok) {
                console.warn('Servidor admin indisponível para auto-vínculo.');
              } else {
                const resp = await adminPost('/link-user', { email: user.email, role: 'vereador' });
                if (resp.ok) {
                  // Recarrega token para obter novos claims
                  await user.getIdToken(true);
                  const refreshed = await getIdTokenResult(user);
                  const refreshedClaims = refreshed.claims as Record<string, unknown>;
                  const rc = refreshedClaims['councilorId'];
                  const newCid = typeof rc === 'string' ? rc : undefined;
                  if (newCid) {
                    setCouncilorId(newCid);

                    // Reassina dados do vereador
                    if (unsubCouncilor) unsubCouncilor();
                    unsubCouncilor = onSnapshot(
                      doc(db, 'councilors', newCid),
                      (snap) => {
                        if (!snap.exists()) return;
                        const data = snap.data() as Omit<Councilor, 'id'>;
                        setCouncilor({
                          id: snap.id,
                          name: data.name,
                          party: data.party,
                          totalLimit: data.totalLimit,
                          position: 'Vereador',
                          photoUrl: data.photoUrl || '/src/assets/parlamentar-photo.jpg',
                        });
                      },
                      (err) => console.warn('Falha no listener do vereador.', err)
                    );

                    // Reassina emendas do vereador
                    if (unsubAmendments) unsubAmendments();
                    const q = query(
                      collection(db, 'amendments'),
                      where('councilorId', '==', newCid)
                    );
                    unsubAmendments = onSnapshot(
                      q,
                      (snap) => {
                        const insts = institutionsRef.current;
                        const amends: Amendment[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Amendment, 'id'>) }));
                        const view = amends.map((a) => ({
                          ...a,
                          institutionName: insts.find((i) => i.id === a.institutionId)?.name || '—',
                        }));
                        amendmentsRef.current = view;
                        setAmendments(view);
                      },
                      (err) => console.warn('Falha no listener de emendas.', err)
                    );

                    linked = true;
                  }
                }
              }
            }
          } catch (e) {
            console.warn('Auto-vínculo por email falhou. Verifique servidor admin e councilors.email.', e);
          }

          if (!linked) {
            toast({
              title: 'Configuração ausente',
              description: 'Seu usuário não está vinculado a um vereador. Contate o administrador.',
            });
          }
        }
      } catch (e) {
        console.error('Erro ao inicializar assinaturas:', e);
        toast({ title: 'Erro', description: 'Falha ao conectar ao Firestore.', variant: 'destructive' });
      }
    });

    return () => {
      unsubAuth && unsubAuth();
      unsubInstitutions && unsubInstitutions();
      unsubSettings && unsubSettings();
      unsubCouncilor && unsubCouncilor();
      unsubAmendments && unsubAmendments();
    };
  }, [navigate, toast]);

  // Quando usuário seleciona uma instituição, abrir diálogo se já existir emenda do vereador
  useEffect(() => {
    if (!selectedInstitutionId) {
      setExistingForSelected(null);
      return;
    }
    const exist = amendmentsRef.current.find((a) => a.institutionId === selectedInstitutionId) || null;
    setExistingForSelected(exist);
    if (exist) setDuplicateDialogOpen(true);
  }, [selectedInstitutionId]);

  const handleNewAllocation = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (allocationsLocked) {
      toast({
        title: "Alocações bloqueadas",
        description: "O prazo para modificações foi encerrado pelo administrador.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData(e.currentTarget);
    const institutionId = formData.get('institution') as string;
    const value = formData.get('allocationValue') as string;
    const description = formData.get('description') as string;

    // Remove formatação monetária para obter valor numérico
    const cents = parseInt((value || '').replace(/\D/g, '')) || 0;
    const numericValue = cents / 100;

    // Validações de entrada
    if (!institutionId) {
      toast({ title: 'Selecione a instituição', description: 'Escolha uma instituição válida.', variant: 'destructive' });
      return;
    }
    if (!validateDescriptionFormat(description)) {
      toast({ title: 'Descrição inválida', description: 'Use 3–600 caracteres; apenas letras, números e pontuação simples.', variant: 'destructive' });
      return;
    }
    if (numericValue <= 0) {
      toast({ title: 'Valor inválido', description: 'O valor deve ser maior que zero.', variant: 'destructive' });
      return;
    }
    const totalAllocatedCheck = amendments.reduce((sum, amendment) => sum + amendment.value, 0);
    const remainingLimitCheck = councilor.totalLimit - totalAllocatedCheck;
    if (numericValue > remainingLimitCheck) {
      toast({ title: 'Limite excedido', description: 'O valor informado ultrapassa o limite disponível.', variant: 'destructive' });
      return;
    }

    // Regra: pelo menos 50% do limite total deve ser destinado à Saúde
    // Enquanto Saúde não atingir 50%, alocações em outras categorias não podem ultrapassar 50% do limite
    const totals = computeHealthTotals(amendments, institutions, councilor.totalLimit);
    const selectedInstitution = institutions.find((i) => i.id === institutionId);
    const targetIsHealth = isHealthCategory(selectedInstitution?.category);
    if (wouldBreakHealthRule(totals, targetIsHealth, targetIsHealth ? 0 : numericValue)) {
      toast({
        title: 'Regra de Saúde não atendida',
        description:
          `Enquanto Saúde não atingir R$ ${totals.healthMinimum.toLocaleString('pt-BR')}, ` +
          `alocações em outras categorias não podem ultrapassar esse limite. ` +
          `Atual fora de Saúde: R$ ${totals.nonHealthAllocated.toLocaleString('pt-BR')}. ` +
          `Valor solicitado: R$ ${numericValue.toLocaleString('pt-BR')}.`,
        variant: 'destructive',
      });
      return;
    }

    // Bloqueio de duplicidade: se já existir emenda para a instituição, oferecer edição/cancelamento
    const duplicate = amendments.find((a) => a.institutionId === institutionId) || null;
    if (duplicate) {
      // Log de tentativa de duplicidade
      await logRuleViolation({
        uid: auth.currentUser?.uid,
        councilorId: councilorId!,
        institutionId,
        attemptedValue: numericValue,
        currentAllocated: amendments.reduce((sum, a) => sum + a.value, 0),
        totalLimit: councilor.totalLimit,
        type: 'duplicate_attempt',
        message: 'Tentativa de criar emenda duplicada para a mesma instituição.',
        dateTime: new Date().toISOString(),
      });
      setSelectedInstitutionId(institutionId);
      setDuplicateDialogOpen(true);
      toast({
        title: 'Instituição já possui emenda',
        description: 'Edite a emenda existente ou cancele a operação.',
      });
      return;
    }

    let cid = councilorId;
    if (!cid && (userUid || auth.currentUser)) {
      try {
        const uid = userUid || auth.currentUser?.uid || '';
        cid = await getUserCouncilorId(uid);
      } catch {
        try {
          const token = auth.currentUser ? await getIdTokenResult(auth.currentUser) : undefined;
          const tClaims = (token?.claims ?? {}) as Record<string, unknown>;
          const tv = tClaims['councilorId'];
          cid = typeof tv === 'string' ? tv : undefined;
        } catch { void 0; }
      }
    }

    if (institutionId && numericValue > 0 && description && cid) {
      let allocationConfirmed = false;
      const today = todayIsoDateCuiaba();
      // Toast de progresso: mantém aberto até receber sucesso/erro
      const progressToast = toast({
        title: 'Gravando no banco de dados',
        description: 'Aguarde enquanto salvamos a emenda...',
        duration: 1000000,
      });
      try {
        const res = await addOrMergeAmendmentSecure({
          councilorId: cid,
          institutionId,
          description,
          value: numericValue,
          status: 'Em andamento',
          date: today,
        });
        const institution = institutions.find((i) => i.id === institutionId);
        // Atualiza toast de progresso com sucesso e fecha automaticamente após breve período
        progressToast.update({
          title: res.action === 'merged' ? 'Valor somado à emenda existente' : 'Emenda alocada com sucesso',
          description: res.action === 'merged'
            ? `Novo total em ${institution?.name}: ${(res.newValue || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
            : `${numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} alocados para ${institution?.name}`,
          duration: 4000,
        });
        allocationConfirmed = true;
        e.currentTarget.reset();
        return;
      } catch {
        // Em alguns cenários a escrita pode concluir no backend, mas o cliente recebe erro/transiente.
        // Mantém mensagem de progresso e tenta confirmar a gravação consultando o Firestore.
        progressToast.update({
          title: 'Confirmando gravação...',
          description: 'Houve instabilidade na confirmação. Verificando o status da emenda...',
          duration: 1000000,
        });

        // Valor anterior para detecção de fusão
        const prevInstitutionAmendment = amendmentsRef.current.find((a) => a.institutionId === institutionId) || null;
        const prevValue = prevInstitutionAmendment?.value ?? 0;

        try {
          // 1) Aguarda a chegada do dado via onSnapshot para evitar falso erro
          const nowFn = props?.testOverrides?.now ?? Date.now;
          const appearsInRealtime = await new Promise<boolean>((resolve) => {
            const deadline = nowFn() + 8000; // até 8s
            const tick = () => {
              const found = amendmentsRef.current.some((a) => {
                if (a.institutionId !== institutionId) return false;
                // Caso de criação nova (match exato)
                const createdMatch = a.description === description && a.value === numericValue && a.date === today;
                // Caso de fusão (valor atual >= valor anterior + novo valor)
                const mergedMatch = a.value >= prevValue + numericValue - 0.0001;
                return createdMatch || mergedMatch;
              });
              if (found) return resolve(true);
              if (nowFn() > deadline) return resolve(false);
              setTimeout(tick, 250);
            };
            setTimeout(tick, 500);
          });

          if (appearsInRealtime) {
            const institution = institutions.find((i) => i.id === institutionId);
            const mergedRealtime = amendmentsRef.current.find((a) => a.institutionId === institutionId) || null;
            const desc = mergedRealtime
              ? `Novo total em ${institution?.name}: ${mergedRealtime.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
              : `${numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} alocados para ${institution?.name}`;
            progressToast.update({ title: 'Emenda confirmada', description: desc, duration: 4000 });
            allocationConfirmed = true;
            e.currentTarget.reset();
            return;
          } else {
            // 2) Fallback: consulta direta ao Firestore para confirmar se o registro existe
            let foundViaFetch = false;
            let latest: Amendment[] | null = null;
            for (let attempt = 0; attempt < 2 && !foundViaFetch; attempt++) {
              try {
                latest = await listAmendmentsByCouncilor(cid);
                foundViaFetch = (latest || []).some((a) => {
                  if (a.institutionId !== institutionId) return false;
                  const createdMatch = a.description === description && a.value === numericValue && a.date === today;
                  const mergedMatch = a.value >= prevValue + numericValue - 0.0001;
                  return createdMatch || mergedMatch;
                });
                if (!foundViaFetch) {
                  const waitFn = props?.testOverrides?.wait;
                  if (waitFn) {
                    await waitFn(500);
                  } else {
                    await new Promise((r) => setTimeout(r, 500));
                  }
                }
              } catch { void 0; }
            }

            if (foundViaFetch) {
              const institution = institutions.find((i) => i.id === institutionId);
              const mergedFetch = (latest || []).find((a) => a.institutionId === institutionId) || null;
              const desc = mergedFetch
                ? `Novo total em ${institution?.name}: ${mergedFetch.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
                : `${numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} alocados para ${institution?.name}`;
              progressToast.update({ title: 'Emenda confirmada', description: desc, duration: 4000 });
              allocationConfirmed = true;
              e.currentTarget.reset();
              return;
            } else {
              if (!allocationConfirmed) {
                progressToast.update({
                  title: 'Erro ao alocar emenda',
                  description: 'Tente novamente mais tarde.',
                  variant: 'destructive',
                  duration: 5000,
                });
              }
            }
          }
        } catch {
          if (!allocationConfirmed) {
            progressToast.update({
              title: 'Erro ao alocar emenda',
              description: 'Tente novamente mais tarde.',
              variant: 'destructive',
              duration: 5000,
            });
          }
        }
      }
    }
  };

  const handleEditAmendment = async (amendmentId: string, newValue: string, newDescription: string) => {
    if (allocationsLocked) {
      toast({
        title: "Edição bloqueada",
        description: "O prazo para modificações foi encerrado pelo administrador.",
        variant: "destructive",
      });
      return;
    }

    const cents = parseInt((newValue || '').replace(/\D/g, '')) || 0;
    const numericValue = cents / 100;
    // Validação da regra de 50% Saúde na edição (troca de valor)
    const targetAmendment = amendments.find((a) => a.id === amendmentId);
    const totalsEdit = computeHealthTotals(amendments, institutions, councilor.totalLimit);
    const targetInstitution = targetAmendment ? institutions.find((i) => i.id === targetAmendment.institutionId) : undefined;
    const targetIsHealth = isHealthCategory(targetInstitution?.category);
    if (targetAmendment && wouldBreakHealthRule(totalsEdit, targetIsHealth, targetIsHealth ? 0 : (numericValue - targetAmendment.value))) {
      toast({
        title: 'Regra de Saúde não atendida',
        description:
          `Enquanto Saúde não atingir R$ ${totalsEdit.healthMinimum.toLocaleString('pt-BR')}, ` +
          `alocações em outras categorias não podem ultrapassar esse limite. ` +
          `Atual fora de Saúde: R$ ${totalsEdit.nonHealthAllocated.toLocaleString('pt-BR')}. ` +
          `Valor proposto: ${numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`,
        variant: 'destructive',
      });
      return;
    }
    // Validação de limite total ao editar
    const currentTotal = amendments.reduce((sum, a) => sum + a.value, 0);
    const self = amendments.find((a) => a.id === amendmentId);
    const proposedTotal = currentTotal - (self?.value || 0) + numericValue;
    if (proposedTotal > councilor.totalLimit) {
      await logRuleViolation({
        uid: auth.currentUser?.uid,
        councilorId: councilor.id,
        institutionId: self?.institutionId,
        attemptedValue: numericValue,
        currentAllocated: currentTotal,
        totalLimit: councilor.totalLimit,
        type: 'limit_exceeded',
        message: 'Tentativa de editar emenda excedendo o limite disponível.',
        dateTime: new Date().toISOString(),
      });
      toast({ title: 'Limite excedido', description: 'O novo total ultrapassa o limite disponível.', variant: 'destructive' });
      return;
    }
    try {
      if (!validateDescriptionFormat(newDescription)) {
        toast({ title: 'Descrição inválida', description: 'Use 3–600 caracteres; apenas letras, números e pontuação simples.', variant: 'destructive' });
        return;
      }
      await updateAmendment(amendmentId, { value: numericValue, description: newDescription });
      const updated = amendments.map((amendment) =>
        amendment.id === amendmentId ? { ...amendment, value: numericValue, description: newDescription } : amendment
      );
      amendmentsRef.current = updated;
      setAmendments(updated);
    } catch {
      toast({ title: 'Erro ao atualizar emenda', description: 'Tente novamente mais tarde.', variant: 'destructive' });
      return;
    }

    toast({
      title: "Emenda atualizada",
      description: "As alterações foram salvas com sucesso.",
    });

    setEditingAmendment(null);
  };

  const handleDeleteAmendment = async (amendmentId: string) => {
    if (allocationsLocked) {
      toast({
        title: "Exclusão bloqueada",
        description: "O prazo para modificações foi encerrado pelo administrador.",
        variant: "destructive",
      });
      return;
    }

    try {
      await deleteAmendment(amendmentId);
      const filtered = amendments.filter((amendment) => amendment.id !== amendmentId);
      amendmentsRef.current = filtered;
      setAmendments(filtered);
    } catch {
      toast({ title: 'Erro ao excluir emenda', description: 'Tente novamente mais tarde.', variant: 'destructive' });
      return;
    }

    toast({
      title: "Emenda removida",
      description: "A emenda foi removida com sucesso.",
    });
  };

  const handleLogout = async () => {
    try {
      const { signOut } = await import('@/services/auth');
      await signOut();
      toast({ title: 'Logout realizado', description: 'Até logo!' });
      navigate('/login');
    } catch {
      toast({ title: 'Erro no logout', description: 'Tente novamente.', variant: 'destructive' });
    }
  };

  const totalAllocated = amendments.reduce((sum, amendment) => sum + amendment.value, 0);
  const remainingLimit = councilor.totalLimit - totalAllocated;
  // Meta de Saúde (50% do limite total)
  const uiTotals = computeHealthTotals(amendments, institutions, councilor.totalLimit);
  const healthAllocatedUI = uiTotals.healthAllocated;
  const healthMinimumUI = uiTotals.healthMinimum;
  const healthProgressUI = healthMinimumUI > 0 ? Math.min(100, Math.round((healthAllocatedUI / healthMinimumUI) * 100)) : 0;
  const healthMissingUI = Math.max(0, healthMinimumUI - healthAllocatedUI);

  // Celebração quando a meta de Saúde é atingida
  const [showHealthCelebration, setShowHealthCelebration] = useState(false);
  const prevHealthMissingRef = useRef<number>(healthMissingUI);
  const triggerConfetti = () => {
    try {
      const styleId = 'confetti-style';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          @keyframes confetti-fall {
            0% { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
        `;
        document.head.appendChild(style);
      }
      const colors = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#ec4899', '#22c55e'];
      for (let i = 0; i < 60; i++) {
        const piece = document.createElement('div');
        piece.style.position = 'fixed';
        piece.style.left = Math.random() * 100 + 'vw';
        piece.style.top = '-12px';
        piece.style.width = '8px';
        piece.style.height = '14px';
        piece.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        piece.style.opacity = '0.9';
        piece.style.transform = `translateY(0) rotate(${Math.random() * 360}deg)`;
        piece.style.borderRadius = '2px';
        const duration = 2000 + Math.random() * 2000;
        piece.style.animation = `confetti-fall ${duration}ms ease-out forwards`;
        document.body.appendChild(piece);
        setTimeout(() => piece.remove(), duration + 120);
      }
    } catch { void 0; }
  };
  useEffect(() => {
    const prev = prevHealthMissingRef.current;
    if (healthMissingUI <= 0 && prev > 0) {
      setShowHealthCelebration(true);
      // Efeito visual simples de confete, sem dependências externas
      triggerConfetti();
      setTimeout(() => setShowHealthCelebration(false), 5000);
    }
    prevHealthMissingRef.current = healthMissingUI;
  }, [healthMissingUI]);

  // Contador regressivo até o encerramento automático (America/Cuiaba)
  useEffect(() => {
    const active = lockSchedule.enabled && !!lockSchedule.date && !!lockSchedule.time && !allocationsLocked;
    if (!active) {
      setShowCountdown(false);
      return;
    }

    setShowCountdown(true);
    const endMs = cuiabaDateTimeToUtcMillis(lockSchedule.date, lockSchedule.time);

    const formatDelta = (ms: number) => {
      const totalSeconds = Math.max(0, Math.floor(ms / 1000));
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `Tempo restante: ${days} dias ${hours} horas ${minutes} minutos ${seconds} segundos`;
    };

    const tick = () => {
      const nowMs = nowCuiabaUtcMillis();
      const delta = endMs - nowMs;
      if (delta <= 0) {
        setShowCountdown(false);
        return;
      }
      setCountdownText(formatDelta(delta));
      setCountdownUrgent(delta <= 24 * 60 * 60 * 1000);
      setCountdownWarning(delta <= 6 * 60 * 60 * 1000);
    };

    tick();
    const intId = setInterval(tick, 1000);
    return () => clearInterval(intId);
  }, [lockSchedule, allocationsLocked]);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <NavigationHeader userType={(userType ?? 'vereador') as 'admin' | 'vereador'} userName={userName} />
      {/* Header com informações do parlamentar */}
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        <ParliamentarianHeader
          name={councilor.name}
          position={councilor.position}
          party={councilor.party}
          reportTitle="Área de Gestão de Emendas dos Vereadores"
          photoUrl={councilor.photoUrl}
        />

        {/* Status do Sistema */}
        {allocationsLocked && (
          <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-6 flex items-center">
            <i className="fas fa-lock text-destructive text-xl mr-3"></i>
            <div className="flex-1">
              <h3 className="font-semibold text-destructive">Sistema Bloqueado</h3>
              <p className="text-sm text-muted-foreground">
                O prazo para alocação e modificação de emendas foi encerrado pelo administrador.
              </p>
            </div>
          </div>
        )}

        {/* Resumo Financeiro */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-primary/5 rounded-lg">
                <i className="fas fa-wallet text-3xl text-primary mb-2"></i>
                <p className="text-sm text-muted-foreground">Limite Total</p>
                <p className="text-2xl font-bold text-foreground">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(councilor.totalLimit)}
                </p>
              </div>
              <div className="text-center p-4 bg-accent/5 rounded-lg">
                <i className="fas fa-chart-pie text-3xl text-accent mb-2"></i>
                <p className="text-sm text-muted-foreground">Total Alocado</p>
                <p className="text-2xl font-bold text-foreground">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalAllocated)}
                </p>
              </div>
              <div className="text-center p-4 bg-success/5 rounded-lg">
                <i className="fas fa-coins text-3xl text-success mb-2"></i>
                <p className="text-sm text-muted-foreground">Disponível</p>
                <p className="text-2xl font-bold text-success">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(remainingLimit)}
                </p>
                {showCountdown && (
                  <p
                    className={`mt-2 text-xs sm:text-sm ${countdownUrgent ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}
                    aria-live="polite"
                  >
                    <i className="fas fa-hourglass-half mr-1"></i>
                    {countdownWarning && (
                      <span className="mr-1 text-amber-600 dark:text-amber-400">Atenção: últimas 6 horas.</span>
                    )}
                    {countdownText}
                  </p>
                )}
              </div>
            </div>
            {/* Barra de progresso da meta de Saúde (50%) */}
            <div className="mt-6 p-4 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <i className="fas fa-heart-pulse text-destructive"></i>
                  <p className="text-sm font-medium">Meta de Saúde (50% + R$ 1,00)</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(healthAllocatedUI)} / {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(healthMinimumUI)}
                </p>
              </div>
              <Progress value={healthProgressUI} />
              <div className="mt-2 text-xs text-muted-foreground">
                {healthMissingUI > 0 ? (
                  <span>
                    Falta {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(healthMissingUI)} para atingir a meta.
                  </span>
                ) : (
                  <div className={`flex items-start gap-3 p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100 ${showHealthCelebration ? 'ring-2 ring-emerald-400 animate-pulse' : ''}`}>
                    <span className="text-2xl">🎉</span>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-emerald-900">Parabéns! Meta Atingida!</p>
                      <p className="text-xs text-emerald-900">
                        Você garantiu o mínimo de 50% para Saúde. Excelente trabalho! Continue priorizando políticas públicas de Saúde.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Nova Alocação */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>
                  <i className="fas fa-plus-circle mr-2"></i>
                  Nova Emenda
                </CardTitle>
                <CardDescription>
                  {allocationsLocked ? (
                    <span className="text-destructive">
                      <i className="fas fa-lock mr-1"></i>
                      Sistema bloqueado
                    </span>
                  ) : (
                    "Aloque recursos para instituições"
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleNewAllocation} className="space-y-4">
                  <div>
                    <Label htmlFor="institution">Instituição</Label>
                    {/* Filtro de Categoria */}
                    <div className="mt-2 mb-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="categoryFilter" className="text-xs">Categoria</Label>
                        <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v)}>
                          <SelectTrigger id="categoryFilter" className="w-full">
                            <SelectValue placeholder="Todas" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas</SelectItem>
                            {realCategories.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {/* Campo oculto para submissão do formulário */}
                    <input type="hidden" name="institution" value={selectedInstitutionId} required />
                    <Popover open={institutionOpen} onOpenChange={setInstitutionOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={institutionOpen}
                          aria-controls="institution-combobox"
                          className="w-full justify-between items-start h-auto min-h-[2.5rem] wrap-long-content"
                          disabled={allocationsLocked}
                        >
                          {selectedInstitutionId ? (() => {
                            const inst = institutions.find((i) => i.id === selectedInstitutionId);
                            const colors = getCategoryColors(inst?.category);
                            return (
                              <span className="flex items-start gap-1 flex-wrap">
                                <span className="flex-1 min-w-0 break-words whitespace-normal leading-snug">{inst?.name ?? 'Selecione a instituição'}</span>
                                {inst?.category && (
                                  <Badge
                                    variant="secondary"
                                    className={`basis-full mt-1 ${colors.bgClass} ${colors.textClass} border ${colors.borderClass} ${colors.hoverClass}`}
                                  >
                                    <i className={`fas ${colors.icon || 'fa-tags'} mr-1`}></i>
                                    {colors.name}
                                  </Badge>
                                )}
                              </span>
                            );
                          })() : 'Selecione a instituição'}
                          <i className="fas fa-chevron-down h-4 w-4 opacity-50"></i>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent id="institution-combobox" className="p-0 w-[min(100vw-2rem,24rem)]">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Digite parte do nome..."
                            value={institutionQuery}
                            onValueChange={setInstitutionQuery}
                          />
                          {/* <div className="flex flex-wrap gap-2 px-3 py-2 border-b">
                            <Button
                              type="button"
                              size="sm"
                              variant={filterByInitial ? 'default' : 'outline'}
                              aria-pressed={filterByInitial}
                              onClick={() => setFilterByInitial((v) => !v)}
                            >
                              Inicial
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={phoneticMode ? 'default' : 'outline'}
                              aria-pressed={phoneticMode}
                              onClick={() => setPhoneticMode((v) => !v)}
                            >
                              Fonetica
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={halfMatchMode ? 'default' : 'outline'}
                              aria-pressed={halfMatchMode}
                              onClick={() => setHalfMatchMode((v) => !v)}
                            >
                              Metades
                            </Button>
                          </div> */}
                          {isSearchingInstitution && (
                            <div className="px-3 py-2 text-xs text-muted-foreground flex items-center">
                              <i className="fas fa-spinner animate-spin mr-2"></i>
                              Buscando...
                            </div>
                          )}
                          <CommandList>
                            <CommandEmpty>Nenhuma instituição encontrada.</CommandEmpty>
                            <CommandGroup heading="Instituições">
                              {filteredInstitutions.map((inst) => (
                                <CommandItem
                                  key={inst.id}
                                  value={inst.name}
                                  onSelect={() => {
                                    setSelectedInstitutionId(inst.id);
                                    setInstitutionOpen(false);
                                  }}
                                >
                                  <div className="flex items-start gap-1 flex-wrap w-full">
                                    <span className="flex-1 min-w-0 break-words whitespace-normal leading-snug">{inst.name}</span>
                                    {inst.category && (() => {
                                      const colors = getCategoryColors(inst.category);
                                      return (
                                        <Badge
                                          variant="secondary"
                                          className="basis-full mt-1"
                                          style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
                                        >
                                          {colors.name}
                                        </Badge>
                                      );
                                    })()}
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label htmlFor="allocationValue">Valor da Emenda</Label>
                    <MoneyInput
                      id="allocationValue"
                      name="allocationValue"
                      placeholder="R$ 0"
                      required
                      disabled={allocationsLocked}
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Descrição</Label>
                    <div className="flex flex-wrap gap-2">
                      <Input
                        id="description"
                        name="description"
                        placeholder="Finalidade da emenda..."
                        required
                        disabled={allocationsLocked}
                        value={desc}
                        onChange={(e) => setDesc(e.target.value)}
                        className="flex-1 min-w-0"
                      />
                      <Button type="button" variant="outline" onClick={() => setIsTemplateDialogOpen(true)} disabled={allocationsLocked}>
                        <i className="fas fa-list mr-1"></i>
                        Modelos
                      </Button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={allocationsLocked || !!existingForSelected}>
                    <i className="fas fa-check-circle mr-2"></i>
                    Alocar Emenda
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Lista de Emendas */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  <i className="fas fa-list-alt mr-2"></i>
                  Minhas Emendas Alocadas
                </CardTitle>
                <CardDescription>
                  Gerencie suas alocações realizadas
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {amendments.length === 0 ? (
                    <div className="text-center py-12">
                      <i className="fas fa-inbox text-6xl text-muted-foreground/30 mb-4"></i>
                      <p className="text-lg text-muted-foreground">Nenhuma emenda alocada ainda</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Use o formulário ao lado para alocar sua primeira emenda
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Navegação por categorias reais (dinâmica, fiel ao cadastro) */}
                      <div role="tablist" aria-label="Categorias de emendas" className="flex flex-wrap gap-2 mb-4">
                        <button
                          key={'all'}
                          id={'tab-all'}
                          role="tab"
                          aria-selected={selectedRealCategory === 'all'}
                          aria-controls={`section-all`}
                          tabIndex={0}
                          onClick={() => setSelectedRealCategory('all')}
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-muted text-foreground`}
                          title={'Todas as categorias'}
                        >
                          <i className="fas fa-layer-group"></i>
                          <span className="text-sm">Todas</span>
                          <span className="ml-1 text-xs bg-black/10 rounded px-1">{amendments.length}</span>
                        </button>
                        {realCategories.map((cat) => {
                          const count = (groupedByRealCategory[cat] || []).length;
                          if (count === 0) return null;
                          const colors = getCategoryColors(cat);
                          return (
                            <button
                              key={cat}
                              id={`tab-${cat}`}
                              role="tab"
                              aria-selected={selectedRealCategory === cat}
                              aria-controls={`section-${cat}`}
                              tabIndex={0}
                              onClick={() => setSelectedRealCategory(cat)}
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-ring border ${colors.bgClass} ${colors.textClass} ${colors.borderClass} ${colors.hoverClass}`}
                              title={colors.name}
                            >
                              <i className={`fas ${colors.icon || 'fa-tags'}`}></i>
                              <span className="text-sm">{colors.name}</span>
                              <span className="ml-1 text-xs bg-black/10 rounded px-1">{count}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Seções por categoria real */}
                      {selectedRealCategory === 'all' ? (
                        <div id="section-all" role="tabpanel" aria-labelledby="tab-all">
                          {realCategories
                            .filter((cat) => (groupedByRealCategory[cat] || []).length > 0)
                            .map((cat) => {
                              const colors = getCategoryColors(cat);
                              return (
                                <section key={cat} id={`section-${cat}`} className="mb-8" aria-labelledby={`tab-${cat}`}>
                                  <div className="flex items-center mb-4">
                                    <div className={`p-3 rounded-lg mr-3 border ${colors.bgClass} ${colors.textClass} ${colors.borderClass}`}>
                                      <i className={`fas ${colors.icon || 'fa-tags'} text-xl`}></i>
                                    </div>
                                    <div>
                                      <h3 className={`text-lg font-bold ${colors.textClass}`}>{colors.name}</h3>
                                      <p className="text-xs text-muted-foreground">{(groupedByRealCategory[cat] || []).length} emendas</p>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {(groupedByRealCategory[cat] || []).map((amendment) => (
                                      <div
                                        key={amendment.id}
                                        className={`border rounded-lg p-3 bg-card text-card-foreground transition-shadow cursor-pointer hover:shadow-md ${editingAmendment === amendment.id ? 'ring-2 ring-primary shadow-lg shadow-primary/30' : ''}`}
                                        onClick={() => {
                                          setSelectedAmendmentDetail(amendment);
                                          setDetailModalOpen(true);
                                        }}
                                      >
                                        <div className="flex justify-between items-start mb-2">
                                          <div className="flex-1">
                                            <h4 className="font-medium text-card-foreground leading-tight">{amendment.institutionName}</h4>
                                            <p className="text-xs text-muted-foreground">{amendment.description}</p>
                                          </div>
                                          <Badge variant={amendment.status === 'Aprovada' ? 'default' : 'secondary'}>
                                            {amendment.status}
                                          </Badge>
                                        </div>
                                        <div className="flex flex-wrap justify-between items-center gap-2">
                                          <div>
                                            <span className="text-lg font-bold text-accent">
                                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amendment.value)}
                                            </span>
                                            <p className="text-xs text-muted-foreground mt-1">
                                              Alocada em: {formatIsoDateToPtBr(amendment.date)}
                                            </p>
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {editingAmendment === amendment.id && (
                                              <Badge variant="secondary" className="animate-pulse" aria-live="polite">Editando…</Badge>
                                            )}
                                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setEditingAmendment(amendment.id); }} disabled={allocationsLocked} aria-label="Editar emenda">
                                              <i className="fas fa-edit mr-1"></i>
                                              Editar
                                            </Button>
                                            <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDeleteAmendment(amendment.id); }} disabled={allocationsLocked} aria-label="Excluir emenda">
                                              <i className="fas fa-trash mr-1"></i>
                                              Excluir
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </section>
                              );
                            })}
                        </div>
                      ) : (
                        realCategories
                          .filter((cat) => cat === selectedRealCategory)
                          .filter((cat) => (groupedByRealCategory[cat] || []).length > 0)
                          .map((cat) => {
                            const colors = getCategoryColors(cat);
                            return (
                              <section key={cat} id={`section-${cat}`} role="tabpanel" aria-labelledby={`tab-${cat}`} className="mb-8">
                                <div className="flex items-center mb-4">
                                  <div className={`p-3 rounded-lg mr-3 border ${colors.bgClass} ${colors.textClass} ${colors.borderClass}`}>
                                    <i className={`fas ${colors.icon || 'fa-tags'} text-xl`}></i>
                                  </div>
                                  <div>
                                    <h3 className={`text-lg font-bold ${colors.textClass}`}>{colors.name}</h3>
                                    <p className="text-xs text-muted-foreground">{(groupedByRealCategory[cat] || []).length} emendas</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {(groupedByRealCategory[cat] || []).map((amendment) => (
                                    <div
                                      key={amendment.id}
                                      className={`border rounded-lg p-3 bg-card text-card-foreground transition-shadow cursor-pointer hover:shadow-md ${editingAmendment === amendment.id ? 'ring-2 ring-primary shadow-lg shadow-primary/30' : ''}`}
                                      onClick={() => {
                                        setSelectedAmendmentDetail(amendment);
                                        setDetailModalOpen(true);
                                      }}
                                    >
                                      <div className="flex justify-between items-start mb-2">
                                        <div className="flex-1 min-w-0">
                                          <h4 className="font-medium text-card-foreground leading-tight truncate">{amendment.institutionName}</h4>
                                          <p className="text-xs text-muted-foreground">{amendment.description}</p>
                                        </div>
                                        <Badge variant={amendment.status === 'Aprovada' ? 'default' : 'secondary'}>
                                          {amendment.status}
                                        </Badge>
                                      </div>
                                      <div className="flex flex-wrap justify-between items-center gap-2">
                                        <div>
                                          <span className="text-lg font-bold text-accent">
                                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amendment.value)}
                                          </span>
                                          <p className="text-xs text-muted-foreground mt-1">
                                            Alocada em: {formatIsoDateToPtBr(amendment.date)}
                                          </p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {editingAmendment === amendment.id && (
                                            <Badge variant="secondary" className="animate-pulse" aria-live="polite">Editando…</Badge>
                                          )}
                                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setEditingAmendment(amendment.id); }} disabled={allocationsLocked} aria-label="Editar emenda">
                                            <i className="fas fa-edit mr-1"></i>
                                            Editar
                                          </Button>
                                          <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); handleDeleteAmendment(amendment.id); }} disabled={allocationsLocked} aria-label="Excluir emenda">
                                            <i className="fas fa-trash mr-1"></i>
                                            Excluir
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </section>
                            );
                          })
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Dialogo para selecionar modelos na criação */}
        <CommandDialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
          <CommandInput placeholder="Buscar modelos de descrição..." />
          <CommandList>
            <CommandEmpty>Nenhum modelo ativo encontrado.</CommandEmpty>
            <CommandGroup heading="Modelos de Descrição">
              {templates.filter((t) => t.active).map((t) => (
                <CommandItem key={t.id} onSelect={() => { setDesc(t.text); setIsTemplateDialogOpen(false); }}>
                  {t.text}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </CommandDialog>

        {/* Dialogo para selecionar modelos na edição */}
        <CommandDialog
          open={!!editTemplateDialogFor}
          onOpenChange={(open) => setEditTemplateDialogFor(open ? editTemplateDialogFor : null)}
        >
          <CommandInput placeholder="Buscar modelos de descrição..." />
          <CommandList>
            <CommandEmpty>Nenhum modelo ativo encontrado.</CommandEmpty>
            <CommandGroup heading="Modelos de Descrição">
              {templates.filter((t) => t.active).map((t) => (
                <CommandItem
                  key={t.id}
                  onSelect={() => {
                    const targetId = editTemplateDialogFor ? `edit-desc-${editTemplateDialogFor}` : '';
                    const el = targetId ? (document.getElementById(targetId) as HTMLInputElement | null) : null;
                    if (el) el.value = t.text;
                    setEditTemplateDialogFor(null);
                  }}
                >
                  {t.text}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </CommandDialog >

        {/* Diálogo de duplicidade: emenda já existente para a instituição selecionada */}
        <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Instituição já possui emenda</DialogTitle>
              {existingForSelected && (
                <DialogDescription>
                  {(() => {
                    const inst = institutionsRef.current.find((i) => i.id === existingForSelected.institutionId);
                    return `Você já alocou R$ ${existingForSelected.value.toLocaleString('pt-BR')} para ${inst?.name}.`;
                  })()}
                </DialogDescription>
              )}
            </DialogHeader>
            {existingForSelected && (
              <div className="space-y-2 text-sm">
                <p><strong>Descrição atual:</strong> {existingForSelected.description}</p>
                <p><strong>Data:</strong> {formatIsoDateToPtBr(existingForSelected.date)}</p>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="default"
                onClick={() => {
                  if (existingForSelected) {
                    setDuplicateDialogOpen(false);
                    setEditingAmendment(existingForSelected.id);
                  }
                }}
              >
                <i className="fas fa-edit mr-1"></i>
                Editar Emenda Existente
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setDuplicateDialogOpen(false);
                  setSelectedInstitutionId('');
                }}
              >
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Detalhes da Emenda */}
        {selectedAmendmentDetail && (
          <InstitutionAmendmentModal
            open={detailModalOpen}
            onOpenChange={(open) => {
              setDetailModalOpen(open);
              if (!open) setTimeout(() => setSelectedAmendmentDetail(null), 200);
            }}
            amendment={selectedAmendmentDetail}
            institution={institutionsRef.current.find(i => i.id === selectedAmendmentDetail.institutionId) || null}
            links={(() => {
              const inst = institutionsRef.current.find(i => i.id === selectedAmendmentDetail.institutionId);
              if (!inst) return [];
              const links = [];
              if (inst.social?.facebook) links.push({ label: 'Facebook', url: inst.social.facebook });
              if (inst.social?.instagram) links.push({ label: 'Instagram', url: inst.social.instagram });
              if (inst.social?.x) links.push({ label: 'X', url: inst.social.x });
              if (inst.social?.tiktok) links.push({ label: 'TikTok', url: inst.social.tiktok });
              if (inst.social?.youtube) links.push({ label: 'YouTube', url: inst.social.youtube });
              if (inst.social?.whatsapp) links.push({ label: 'WhatsApp', url: `https://wa.me/${inst.social.whatsapp.replace(/\D/g, '')}` });
              return links;
            })()}
          />
        )}

        {/* Modal de edição centralizado e acessível */}
        {(() => {
          const item = amendments.find((a) => a.id === editingAmendment) || null;
          if (!item) return null;
          return (
            <Dialog open={!!editingAmendment} onOpenChange={(open) => setEditingAmendment(open ? editingAmendment : null)}>
              <DialogContent
                ref={editDialogRef}
                tabIndex={-1}
                className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-[calc(100vw-2rem)] max-w-[680px] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl shadow-2xl bg-[var(--modal-bg)] text-[var(--modal-fg)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 p-[var(--space-md)] md:p-[var(--space-lg)]"
                aria-labelledby="edit-amendment-title"
                aria-describedby="edit-amendment-desc"
                aria-modal="true"
                style={{
                  ['--modal-bg']: 'hsl(var(--card))',
                  ['--modal-fg']: 'hsl(var(--card-foreground))',
                  ['--modal-muted']: 'hsl(var(--muted-foreground))',
                  ['--modal-primary']: 'hsl(var(--primary))',
                  ['--modal-error']: 'hsl(var(--destructive))',
                  ['--space-lg']: '1.25rem',
                  ['--space-md']: '0.75rem',
                } as React.CSSProperties}
              >
                <DialogHeader className="space-y-1">
                  <DialogTitle id="edit-amendment-title" className="text-xl md:text-2xl font-semibold tracking-tight">
                    Edição de Emenda
                  </DialogTitle>
                  <DialogDescription id="edit-amendment-desc" className="text-sm md:text-base text-[var(--modal-muted)]">
                    Edite os dados da emenda selecionada.
                  </DialogDescription>
                </DialogHeader>

                <DialogClose asChild>
                  <button
                    type="button"
                    className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-transparent text-[var(--modal-muted)] hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--modal-primary)]"
                    aria-label="Fechar"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </DialogClose>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <MoneyInput
                      id={`modal-edit-value-${item.id}`}
                      name={`modal-edit-value-${item.id}`}
                      value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.value)}
                      disabled={allocationsLocked}
                      aria-label="Valor da emenda"
                      ariaInvalid={false}
                      className="focus:outline-none focus:ring-2 focus:ring-[var(--modal-primary)] disabled:opacity-60 disabled:cursor-not-allowed aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-[var(--modal-error)]"
                    />
                    <Input
                      id={`modal-edit-desc-${item.id}`}
                      defaultValue={item.description}
                      placeholder="Descrição"
                      disabled={allocationsLocked}
                      aria-label="Descrição da emenda"
                      aria-invalid={false}
                      autoFocus
                      className="focus:outline-none focus:ring-2 focus:ring-[var(--modal-primary)] disabled:opacity-60 disabled:cursor-not-allowed aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-[var(--modal-error)]"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" variant="outline" onClick={() => setEditTemplateDialogFor(item.id)} disabled={allocationsLocked} aria-label="Abrir modelos" className="font-medium">
                      <i className="fas fa-list mr-1"></i>
                      Modelos
                    </Button>
                  </div>
                </div>

                <DialogFooter className="gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      const valueInput = document.getElementById(`modal-edit-value-${item.id}`) as HTMLInputElement;
                      const descInput = document.getElementById(`modal-edit-desc-${item.id}`) as HTMLInputElement;
                      handleEditAmendment(item.id, valueInput?.value || '', descInput?.value || '');
                      setEditingAmendment(null);
                    }}
                    disabled={allocationsLocked}
                    aria-label="Salvar edição"
                    className="bg-[var(--modal-primary)] text-white hover:bg-[var(--modal-primary)]/90 focus-visible:ring-2 focus-visible:ring-[var(--modal-primary)] focus-visible:ring-offset-2"
                  >
                    <i className="fas fa-save mr-1"></i>
                    Salvar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingAmendment(null)} aria-label="Cancelar edição" className="font-medium">
                    Cancelar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          );
        })()}

      </div >
    </div >
  );
}
